import React, {useState, useCallback} from "react";
import {useParams} from "react-router-dom";
import {Box, Button, Typography} from "@mui/material";
import {MainContainer} from "../../components/ContentWrapper";
import {PageTitle} from "../../components/PageTitle";
import {BrandDetailContentTypesPanel} from "./BrandDetailContentTypesPanel";
import axiosInstant from "../../api";
import toast from "react-hot-toast";

export default function BrandContentTypesPage() {
    const {id} = useParams<{id: string}>();
    const [selectedTypeIds, setSelectedTypeIds] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);
    const [isRmn, setIsRmn] = useState<boolean | null>(null);

    React.useEffect(() => {
        if (!id) return;
        axiosInstant
            .get(`/brands/${id}`)
            .then((res) => {
                setIsRmn(!!res.data?.rmn);
            })
            .catch(() => setIsRmn(false));
    }, [id]);

    const handleSelectionChange = useCallback((typeIds: number[]) => {
        setSelectedTypeIds(typeIds);
    }, []);

    const handleSave = async () => {
        if (!id) return;

        setSaving(true);
        try {
            // Channels apply to every content type now — payload is just the
            // selected content types.
            const contentTypesPayload = selectedTypeIds.map((typeId) => ({
                content_type_id: typeId,
            }));

            await axiosInstant.put(`/brands/${id}/content-selections`, {
                content_types: contentTypesPayload,
            });
            toast.success("Content selections saved successfully.");
        } catch (err) {
            console.error("Failed to save selections:", err);
            toast.error("Failed to save content selections.");
        } finally {
            setSaving(false);
        }
    };

    if (!id) {
        return (
            <MainContainer>
                <Typography color="error">Brand ID is required.</Typography>
            </MainContainer>
        );
    }

    return (
        <MainContainer>
            <Box className="flex justify-between items-center mb-6">
                <PageTitle title="Brand Content Types" />
                {isRmn && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Changes"}
                    </Button>
                )}
            </Box>

            <Box className="mb-4">
                <Typography variant="body2" color="textSecondary">
                    Select the content types available for this brand.
                </Typography>
            </Box>

            <BrandDetailContentTypesPanel
                brandId={parseInt(id, 10)}
                onSelectionChange={handleSelectionChange}
            />
        </MainContainer>
    );
}
